import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, KeyRound, Trash2, RefreshCw, Mail, Check, X, Copy, Crown, Briefcase } from "lucide-react";

const ROLE_LABEL = { salon_owner: "Co-titolare", staff: "Collaboratore" };
const ROLE_COLOR = { salon_owner: "#06B6D4", staff: "#3B82F6" };

// Reparti disponibili (usati soprattutto in HQ agenzia). In un salone i membri
// possono anche non selezionarne nessuno — il campo è opzionale.
const DEPARTMENTS = [
  { k: "marketing", label: "Marketing", color: "#EC4899" },
  { k: "coaching", label: "Coaching", color: "#8B5CF6" },
  { k: "commerciale", label: "Commerciale", color: "#F59E0B" },
  { k: "delivery", label: "Delivery", color: "#10B981" },
  { k: "admin", label: "Amministrazione", color: "#3B82F6" },
];
const DEPT_BY = Object.fromEntries(DEPARTMENTS.map((d) => [d.k, d]));

export function MembersTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tenant/members");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeMember = async (u) => {
    if (!window.confirm(`Rimuovere ${u.email} dal team?`)) return;
    try {
      await api.delete(`/tenant/members/${u.id}`);
      toast.success("Membro rimosso");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const resendInvite = async (iid) => {
    try {
      const r = await api.post(`/tenant/members/invites/${iid}/resend`);
      toast.success(r.data.email_sent ? "Invito reinviato" : "Invito rigenerato (email non recapitata)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const cancelInvite = async (iid) => {
    if (!window.confirm("Annullare questo invito?")) return;
    try {
      await api.delete(`/tenant/members/invites/${iid}`);
      toast.success("Invito annullato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (loading || !data) {
    return <div className="sp-card p-8 text-sm text-[color:var(--sp-text-secondary)]">Caricamento…</div>;
  }

  return (
    <div data-testid="members-tab" className="space-y-4">
      <div className="sp-card p-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[color:var(--sp-brand)]" />
            <h2 className="font-display text-xl font-black">Accessi & Permessi</h2>
          </div>
          <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1 max-w-lg">
            Invita collaboratori o co-titolari a Memento. Ogni persona riceve un link email
            per creare la propria password. Scegli tu quali sezioni può usare.
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="invite-member-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--sp-brand)] text-white font-bold hover:opacity-90 whitespace-nowrap">
          <UserPlus className="h-4 w-4" /> Invita membro
        </button>
      </div>

      {/* Members list */}
      <div className="sp-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[color:var(--sp-border)] flex items-center gap-2">
          <span className="label-eyebrow">Team attivo ({data.members.length})</span>
        </div>
        {data.members.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--sp-text-secondary)] text-center">Nessun membro ancora.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.members.map((u) => (
                <tr key={u.id} data-testid={`member-row-${u.id}`} className="border-t border-[color:var(--sp-border)]">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-display font-black text-sm flex-shrink-0"
                           style={{ background: ROLE_COLOR[u.role] || "#94A3B8" }}>
                        {(u.name || u.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold truncate flex items-center gap-1.5">
                          {u.name || u.email}
                          {u.is_owner && <Crown className="h-3.5 w-3.5 text-amber-500" title="Titolare" />}
                        </div>
                        <div className="text-xs text-[color:var(--sp-text-secondary)] truncate">{u.email}</div>
                        {u.departments && u.departments.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1" data-testid={`member-${u.id}-departments`}>
                            {u.departments.map((d) => {
                              const meta = DEPT_BY[d];
                              if (!meta) return null;
                              return (
                                <span key={d} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                                  style={{ background: `${meta.color}22`, color: meta.color }}>
                                  {meta.label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
                          style={{ background: `${ROLE_COLOR[u.role]}18`, color: ROLE_COLOR[u.role] }}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="p-3">
                    {u.is_owner || u.role === "salon_owner" ? (
                      <span className="text-xs text-[color:var(--sp-text-muted)]">Tutti i permessi</span>
                    ) : (
                      <div className="text-xs text-[color:var(--sp-text-secondary)] flex items-center gap-1 flex-wrap">
                        <span className="font-bold">{u.permissions?.length || 0}</span> sezioni
                        {u.permissions?.length > 0 && (
                          <span className="text-[color:var(--sp-text-muted)]">
                            · {u.permissions.slice(0, 3).map((p) => data.sections.find((s) => s.key === p)?.label).filter(Boolean).join(", ")}
                            {u.permissions.length > 3 && ` +${u.permissions.length - 3}`}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {!u.is_owner && (
                      <>
                        <button onClick={() => setEditing(u)} data-testid={`edit-member-${u.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] text-xs font-bold hover:bg-[color:var(--sp-subtle)]">
                          Modifica
                        </button>
                        <button onClick={() => removeMember(u)} data-testid={`delete-member-${u.id}`}
                          className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-red-400 text-red-600 text-xs font-bold hover:bg-red-50">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pending invites */}
      {data.pending_invites.length > 0 && (
        <div className="sp-card overflow-hidden">
          <div className="px-4 py-3 border-b border-[color:var(--sp-border)] flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-amber-600" />
            <span className="label-eyebrow">Inviti in sospeso ({data.pending_invites.length})</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {data.pending_invites.map((inv) => (
                <tr key={inv.id} data-testid={`invite-row-${inv.id}`} className="border-t border-[color:var(--sp-border)]">
                  <td className="p-3">
                    <div className="font-bold text-sm">{inv.name}</div>
                    <div className="text-xs text-[color:var(--sp-text-secondary)]">{inv.email}</div>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <span className="text-xs font-bold" style={{ color: ROLE_COLOR[inv.role] }}>{ROLE_LABEL[inv.role]}</span>
                  </td>
                  <td className="p-3 text-xs text-[color:var(--sp-text-muted)]">
                    Scade il {(inv.expires_at || "").slice(0, 10)}
                    {inv.email_sent === false && <span className="ml-2 text-amber-600 font-bold">(email non recapitata)</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => resendInvite(inv.id)} data-testid={`resend-invite-${inv.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] text-xs font-bold hover:bg-[color:var(--sp-subtle)]">
                      <RefreshCw className="h-3 w-3" /> Rinvia
                    </button>
                    <button onClick={() => cancelInvite(inv.id)} data-testid={`cancel-invite-${inv.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-red-400 text-red-600 text-xs font-bold hover:bg-red-50">
                      <X className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <InviteModal sections={data.sections}
          onClose={() => setShowInvite(false)}
          onDone={() => { setShowInvite(false); load(); }} />
      )}
      {editing && (
        <EditMemberModal member={editing} sections={data.sections}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invite modal
// ---------------------------------------------------------------------------
function InviteModal({ sections, onClose, onDone }) {
  const [form, setForm] = useState({
    email: "", name: "", role: "staff",
    permissions: ["agenda", "clients"],
    departments: [],
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleAll = () => {
    if (form.permissions.length === sections.length) setForm({ ...form, permissions: [] });
    else setForm({ ...form, permissions: sections.map((s) => s.key) });
  };
  const togglePerm = (k) => {
    setForm((f) => f.permissions.includes(k)
      ? { ...f, permissions: f.permissions.filter((x) => x !== k) }
      : { ...f, permissions: [...f.permissions, k] });
  };
  const toggleDept = (k) => {
    setForm((f) => f.departments.includes(k)
      ? { ...f, departments: f.departments.filter((x) => x !== k) }
      : { ...f, departments: [...f.departments, k] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/tenant/members/invite", form);
      setResult(r.data);
      toast.success(r.data.email_sent ? "Invito inviato via email" : "Invito creato (email non recapitata)");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invito"); }
    finally { setBusy(false); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.accept_url);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Impossibile copiare"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={result ? null : onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="invite-modal"
        className="sp-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3">
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <h3 className="font-display text-xl font-black inline-flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[color:var(--sp-brand)]" /> Invita un membro
            </h3>
            <p className="text-xs text-[color:var(--sp-text-secondary)]">
              Riceverà un'email con un link per creare la propria password.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-bold block mb-1">Nome *</span>
                <input required className="sp-input" data-testid="invite-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
              <label className="block"><span className="text-xs font-bold block mb-1">Email *</span>
                <input required type="email" className="sp-input" data-testid="invite-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            </div>
            <div>
              <span className="text-xs font-bold block mb-1">Ruolo</span>
              <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="invite-role-toggle">
                {[["staff", "Collaboratore"], ["salon_owner", "Co-titolare (tutti i permessi)"]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`invite-role-${k}`}
                    onClick={() => setForm({ ...form, role: k })}
                    className={`px-3 py-1 rounded-full text-xs font-bold ${form.role === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {form.role === "staff" && (
              <div className="rounded-xl border border-[color:var(--sp-border)] p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold">Sezioni accessibili ({form.permissions.length}/{sections.length})</span>
                  <button type="button" onClick={toggleAll} className="text-xs text-[color:var(--sp-brand)] font-bold hover:underline">
                    {form.permissions.length === sections.length ? "Deseleziona tutto" : "Seleziona tutto"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {sections.map((s) => (
                    <label key={s.key} data-testid={`invite-perm-${s.key}`}
                      className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 cursor-pointer text-sm ${form.permissions.includes(s.key) ? "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "hover:bg-[color:var(--sp-subtle)]"}`}>
                      <input type="checkbox" checked={form.permissions.includes(s.key)}
                        onChange={() => togglePerm(s.key)} />
                      <span className="text-xs font-bold">{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-[color:var(--sp-border)] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
                <span className="text-xs font-bold">Reparti di lavoro ({form.departments.length})</span>
                <span className="text-[10px] text-[color:var(--sp-text-muted)]">— opzionale</span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="invite-departments">
                {DEPARTMENTS.map((d) => {
                  const on = form.departments.includes(d.k);
                  return (
                    <button key={d.k} type="button" onClick={() => toggleDept(d.k)}
                      data-testid={`invite-dept-${d.k}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border transition ${on
                        ? "text-white"
                        : "bg-white border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:border-[color:var(--sp-brand)]"}`}
                      style={on ? { background: d.color, borderColor: d.color } : undefined}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
              <button type="submit" disabled={busy} data-testid="invite-submit"
                className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
                {busy ? "Invio…" : "Invia invito"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3" data-testid="invite-result">
            <h3 className="font-display text-xl font-black inline-flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-500" /> Invito {result.email_sent ? "inviato" : "creato"}
            </h3>
            <div className={`rounded-lg px-3 py-2 text-xs font-bold ${result.email_sent
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-amber-50 text-amber-800 border border-amber-200"}`}
              data-testid="invite-email-status">
              {result.email_sent
                ? "Email di invito inviata correttamente."
                : "Email non recapitata — copia il link qui sotto e invialo tu."}
            </div>
            <div className="rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)] p-3">
              <div className="text-[10px] label-eyebrow mb-1">Link di invito (valido 7 giorni)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-xs bg-white px-2 py-1 rounded" data-testid="invite-accept-url">{result.accept_url}</code>
                <button type="button" onClick={copyLink} data-testid="invite-copy-link"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[color:var(--sp-brand)] text-white text-xs font-bold hover:opacity-90">
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copiato" : "Copia"}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onDone} data-testid="invite-done"
                className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90">Chiudi</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit member modal
// ---------------------------------------------------------------------------
function EditMemberModal({ member, sections, onClose, onSaved }) {
  const [name, setName] = useState(member.name || "");
  const [email, setEmail] = useState(member.email || "");
  const [phone, setPhone] = useState(member.phone || "");
  const [role, setRole] = useState(member.role);
  const [permissions, setPermissions] = useState(member.permissions || []);
  const [departments, setDepartments] = useState(member.departments || []);
  const [busy, setBusy] = useState(false);
  const togglePerm = (k) => setPermissions((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  const toggleAll = () => setPermissions(permissions.length === sections.length ? [] : sections.map((s) => s.key));
  const toggleDept = (k) => setDepartments((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!email.trim()) { toast.error("Email obbligatoria"); return; }
    setBusy(true);
    try {
      // Costruisce payload solo con i campi effettivamente modificati per
      // evitare aggiornamenti inutili + minimizzare i side effect backend.
      const payload = { role, permissions, departments };
      if (name.trim() !== (member.name || "")) payload.name = name.trim();
      if (email.trim().toLowerCase() !== (member.email || "").toLowerCase()) payload.email = email.trim();
      if (phone.trim() !== (member.phone || "")) payload.phone = phone.trim();
      await api.patch(`/tenant/members/${member.id}`, payload);
      toast.success("Aggiornato");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-member-modal"
        className="sp-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3">
        <h3 className="font-display text-xl font-black inline-flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-[color:var(--sp-brand)]" /> Modifica membro
        </h3>

        {/* Anagrafica */}
        <div className="rounded-xl border border-[color:var(--sp-border)] p-3 space-y-2">
          <div className="text-xs font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">Anagrafica</div>
          <label className="block"><span className="text-xs font-bold block mb-1">Nome *</span>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="edit-name" className="sp-input" /></label>
          <label className="block"><span className="text-xs font-bold block mb-1">Email *</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="edit-email" className="sp-input" />
            {email.trim().toLowerCase() !== (member.email || "").toLowerCase() && (
              <span className="text-[10px] text-amber-700 mt-1 block">
                ⚠ Il cambio email invalida le sessioni attive: dovrà rifare login.
              </span>
            )}
          </label>
          <label className="block"><span className="text-xs font-bold block mb-1">Telefono</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+39 …" data-testid="edit-phone" className="sp-input" /></label>
        </div>

        <div>
          <span className="text-xs font-bold block mb-1">Ruolo</span>
          <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
            {[["staff", "Collaboratore"], ["salon_owner", "Co-titolare"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`edit-role-${k}`}
                onClick={() => setRole(k)}
                className={`px-3 py-1 rounded-full text-xs font-bold ${role === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {role === "staff" && (
          <div className="rounded-xl border border-[color:var(--sp-border)] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold">Sezioni accessibili ({permissions.length}/{sections.length})</span>
              <button type="button" onClick={toggleAll} className="text-xs text-[color:var(--sp-brand)] font-bold hover:underline">
                {permissions.length === sections.length ? "Deseleziona tutto" : "Seleziona tutto"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {sections.map((s) => (
                <label key={s.key} data-testid={`edit-perm-${s.key}`}
                  className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 cursor-pointer text-sm ${permissions.includes(s.key) ? "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "hover:bg-[color:var(--sp-subtle)]"}`}>
                  <input type="checkbox" checked={permissions.includes(s.key)} onChange={() => togglePerm(s.key)} />
                  <span className="text-xs font-bold">{s.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {role === "salon_owner" && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            I co-titolari hanno accesso a <b>tutte</b> le sezioni. Cambierà anche la sessione attiva del membro.
          </div>
        )}
        <div className="rounded-xl border border-[color:var(--sp-border)] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
            <span className="text-xs font-bold">Reparti di lavoro ({departments.length})</span>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="edit-departments">
            {DEPARTMENTS.map((d) => {
              const on = departments.includes(d.k);
              return (
                <button key={d.k} type="button" onClick={() => toggleDept(d.k)}
                  data-testid={`edit-dept-${d.k}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-bold border transition ${on
                    ? "text-white"
                    : "bg-white border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:border-[color:var(--sp-brand)]"}`}
                  style={on ? { background: d.color, borderColor: d.color } : undefined}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="edit-member-save"
            className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
            {busy ? "Salvo…" : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}
